import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { pluginsApi } from "@/api/plugins-api"
import { Button } from "@/components/ui/button"

import { ConnectionGrants } from "./connection-grants"
import { pluginErrorCode, pluginErrorMessage, startAuthorization } from "./plugin-utils"
import { usePluginQueryKey } from "./use-plugin-query-key"

export function BackgroundAccountPanel({ flowId }: { flowId?: string }) {
  const { t } = useTranslation()
  const key = usePluginQueryKey()
  const client = useQueryClient()
  const account = useQuery({
    queryKey: key("background-account"),
    queryFn: pluginsApi.backgroundAccount,
  })
  const connect = useMutation({
    mutationFn: () => pluginsApi.startBackgroundAccount(flowId),
    onSuccess: (result) => startAuthorization(result.authorization_url),
  })
  const disconnect = useMutation({
    mutationFn: pluginsApi.disconnectBackgroundAccount,
    onSuccess: () => client.invalidateQueries({ queryKey: key() }),
  })
  const error = account.error ?? connect.error ?? disconnect.error
  const connected = account.data?.status === "connected"
  const statusText = connected ? t("plugins.connected") : t("plugins.backgroundAccountRequired")
  return (
    <section className="space-y-3 rounded-lg border p-3" data-testid="background-account">
      <h2 className="text-sm font-semibold">{t("plugins.backgroundAccountTitle")}</h2>
      <p className="text-muted-foreground text-sm">{t("plugins.backgroundAccountDescription")}</p>
      <p className="text-sm" role="status">
        {account.isPending ? t("plugins.loading") : statusText}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          disabled={connect.isPending || account.isPending || !!account.error}
          variant="outline"
          onClick={() => connect.mutate()}
        >
          {connected ? t("plugins.reconnect") : t("plugins.backgroundAccountConnect")}
        </Button>
        {account.data?.status !== "disconnected" && account.data?.connection_id && (
          <Button
            disabled={disconnect.isPending}
            variant="outline"
            onClick={() => disconnect.mutate()}
          >
            {t("plugins.disconnect")}
          </Button>
        )}
      </div>
      {connected && !flowId && account.data?.connection_id && (
        <ConnectionGrants connectionId={account.data.connection_id} />
      )}
      {error && (
        <p className="text-destructive text-sm" role="alert">
          {pluginErrorMessage(t, pluginErrorCode(error))}
        </p>
      )}
    </section>
  )
}
